// FieldClimate station data split the way FieldClimate's own tables show it:
// one icon for air measurements, one for wind, ET0 and device health.
// Each aggregate maps to the reading key the backend stores (primary value under the
// plain key, other aggregates as `${key}_max` / `_min`). Tables show max/min, not averages,
// wherever FieldClimate provides them; ET0/DSS still use the averages server-side.

// Aggregate codes are language-neutral; aggLabel() turns them into column labels.
// avg: plain-key average shown only while max/min are not available yet.
const col = (key, tr, en, unit, color, aggs, chart = 'band', avg = null) => ({
    key, tr, en, unit, color, chart, avg,
    aggs: aggs.map(([label, field]) => ({ label, field })),
});

export const FC_GROUPS = {
    weather: {
        tr: 'Hava ölçümleri',
        en: 'Air measurements',
        columns: [
            col('temp', 'Hava sıcaklığı', 'Air temperature', '°C', '#dc2626',
                [['max', 'A.Temperature_max'], ['min', 'A.Temperature_min']], 'band', 'A.Temperature'),
            col('dew', 'Çiy noktası', 'Dew point', '°C', '#0891b2',
                [['min', 'Dew Point_min']], 'band', 'Dew Point'),
            col('rad', 'Güneş radyasyonu', 'Solar radiation', 'W/m²', '#d97706', [['avg', 'S.Radiation']]),
            col('vpd', 'VPD (Açık buhar basıncı)', 'VPD', 'kPa', '#16a34a', [['min', 'VPD_min']], 'band', 'VPD'),
            col('rh', 'Bağıl nem', 'Relative humidity', '%', '#2563eb',
                [['max', 'R_Humidity_max'], ['min', 'R_Humidity_min']], 'band', 'R_Humidity'),
            col('rain', 'Yağış', 'Precipitation', 'mm', '#4f46e5', [['sum', 'Precipitation']], 'bar'),
        ],
    },
    agro: {
        tr: 'Rüzgar, ET₀ ve cihaz',
        en: 'Wind, ET₀ & device',
        columns: [
            col('rain', 'Yağış', 'Precipitation', 'mm', '#4f46e5', [['sum', 'Precipitation']], 'bar'),
            col('wind', 'Rüzgar hızı', 'Wind speed', 'm/s', '#475569', [['max', 'W.Speed_max']], 'band', 'W.Speed'),
            col('gust', 'Rüzgar hamlesi', 'Wind gust', 'm/s', '#334155', [['max', 'W.Gust']]),
            col('battery', 'Batarya', 'Battery', 'mV', '#65a30d', [['last', 'Battery']]),
            col('panel', 'Güneş paneli', 'Solar panel', 'mV', '#ca8a04', [['last', 'Solar Panel']]),
            col('deltat', 'Delta T', 'Delta T', '°C', '#9333ea',
                [['max', 'DeltaT_max'], ['min', 'DeltaT_min']], 'band', 'DeltaT'),
            col('et0', 'ET₀ (günlük)', 'ET₀ (daily)', 'mm', '#0d9488', [['daily', 'ET0']], 'daily'),
        ],
    },
};

const AGG_LABELS = {
    tr: { max: 'max', min: 'min', avg: 'Ort', last: 'Son', sum: 'toplam', daily: 'gün' },
    en: { max: 'max', min: 'min', avg: 'avg', last: 'last', sum: 'sum', daily: 'daily' },
};
export const aggLabel = (code, lang) => AGG_LABELS[lang === 'en' ? 'en' : 'tr'][code] ?? code;

export const groupTitle = (group, lang) => (lang === 'en' ? FC_GROUPS[group].en : FC_GROUPS[group].tr);

const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

// API returns newest first; charts need oldest first.
export function toPoints(readings) {
    return (readings || [])
        .filter(r => r.extra && typeof r.extra === 'object' && Object.keys(r.extra).length > 0)
        .map(r => ({ t: r.recordedAt, ...r.extra }))
        .sort((a, b) => new Date(a.t) - new Date(b.t));
}

// Date inputs give "YYYY-MM-DD" in the viewer's local calendar; the end day is inclusive.
export function filterByDate(pts, start, end) {
    const from = start ? new Date(`${start}T00:00:00`) : null;
    const to = end ? new Date(`${end}T23:59:59.999`) : null;
    return pts.filter(p => {
        const d = new Date(p.t);
        return (!from || d >= from) && (!to || d <= to);
    });
}

// Latest daily ET0 reported by the FieldClimate station placed on a field, if any.
export function stationEt0(sensors, fieldId) {
    const s = (sensors || []).find(x => x.fieldclimateStationId && x.fieldId === fieldId && isNum(x.latestData?.ET0));
    return s ? { value: s.latestData.ET0, at: s.latestData.ET0_at ?? null } : null;
}

// One line per value shown in the table; a shaded band between max and min when both exist.
export function chartSeries(column) {
    const max = column.aggs.find(a => a.label === 'max')?.field;
    const min = column.aggs.find(a => a.label === 'min')?.field;
    return {
        lines: column.aggs.map(a => ({ field: a.field, label: a.label })),
        band: max && min ? { max, min } : null,
    };
}

// How a 30-min bucket combines its 15-min records, by aggregate label.
const REDUCERS = {
    max: vals => Math.max(...vals),
    min: vals => Math.min(...vals),
    sum: vals => vals.reduce((a, b) => a + b, 0),
    avg: vals => vals.reduce((a, b) => a + b, 0) / vals.length,
    last: vals => vals[vals.length - 1],
    daily: vals => vals[vals.length - 1],
};

const HALF_HOUR = 30 * 60 * 1000;

// Station records are 15-min; rows are shown per 30 min, labelled by the end of the window
// (10:15 + 10:30 → 10:30). Turkey's offset is whole hours, so UTC buckets match local ones.
export function aggregate30(pts, columns) {
    const fields = columns.flatMap(c => c.aggs.map(a => [a.field, REDUCERS[a.label]]));
    const buckets = new Map();
    for (const p of pts) {
        const end = Math.ceil(new Date(p.t).getTime() / HALF_HOUR) * HALF_HOUR;
        if (!buckets.has(end)) buckets.set(end, []);
        buckets.get(end).push(p);
    }
    return [...buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([end, group]) => {
            const row = { t: new Date(end).toISOString() };
            for (const [field, reduce] of fields) {
                const vals = group.map(p => p[field]).filter(isNum);
                if (vals.length) row[field] = reduce(vals);
            }
            return row;
        });
}

export function visibleColumns(group, pts) {
    const has = (field) => pts.some(p => isNum(p[field]));
    return group.columns
        .map(c => {
            const aggs = c.aggs.filter(a => has(a.field));
            if (!aggs.length && c.avg && has(c.avg)) return { ...c, aggs: [{ label: 'avg', field: c.avg }] };
            return { ...c, aggs };
        })
        .filter(c => c.aggs.length > 0);
}
